// Digital IO modules (Waveshare Modbus RTU IO 8CH) — separate from scanners.
// Inputs DI1..8 = FC02 discrete inputs 0..7, outputs DO1..8 = FC01/FC05 coils 0..7.

import { API_BASE } from './api-base';

export interface IoModule {
  id: string;
  clientId: string;
  bridgeId?: string | null;
  name: string;
  location?: string;
  description?: string;
  modelId: string;
  modelName: string;
  inputs: number;
  outputs: number;
  connectionType: 'rtu';
  serialPort?: string;
  baudRate?: number;
  dataBits?: number;
  stopBits?: number;
  parity?: 'none' | 'even' | 'odd';
  slaveId: number;
  pollIntervalMs?: number;
  timeoutMs?: number;
  labels: {
    inputs: string[];
    outputs: string[];
    inputVisible?: boolean[];
    outputVisible?: boolean[];
    panel?: string;
  };
  isActive?: boolean;
}

/** What a client sees: only channels the admin marked "Show to client". */
export interface IoClientModule {
  id: string;
  name: string;
  location?: string;
  description?: string;
  modelName: string;
  slaveId: number;
  pollIntervalMs?: number;
  panel: string;
  inputs: { ch: number; name: string }[];
  outputs: { ch: number; name: string }[];
}

export interface IoTestResult {
  ok: boolean;
  error?: string | null;
  link?: string;
  inputs?: boolean[] | null;
  outputs?: boolean[] | null;
  inputsError?: string | null;
  outputsError?: string | null;
  elapsedMs?: number | null;
  roundTripMs?: number;
}

const headers = (token: string) => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

async function request<T>(url: string, token: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, { ...init, headers: { ...headers(token), ...(init.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Request failed (HTTP ${res.status})`);
  return body as T;
}

export const ioApi = {
  listForClient: (token: string, clientId: string) =>
    request<{ modules: IoClientModule[] }>(`/api/clients/${clientId}/io-modules`, token).then((r) => r.modules || []),
  setOutput: (token: string, clientId: string, moduleId: string, channel: number, value: boolean) =>
    request<{ success: boolean; verified: boolean }>(`/api/clients/${clientId}/io-modules/${moduleId}/outputs/${channel}`, token, { method: 'PUT', body: JSON.stringify({ value }) }),
  adminList: (token: string, clientId: string) =>
    request<{ modules: IoModule[] }>(`/api/admin/clients/${clientId}/io-modules`, token).then((r) => r.modules || []),
  create: (token: string, clientId: string, m: Partial<IoModule>) =>
    request<{ module: IoModule }>(`/api/admin/clients/${clientId}/io-modules`, token, { method: 'POST', body: JSON.stringify(m) }).then((r) => r.module),
  update: (token: string, clientId: string, id: string, m: Partial<IoModule>) =>
    request<{ module: IoModule }>(`/api/admin/clients/${clientId}/io-modules/${id}`, token, { method: 'PUT', body: JSON.stringify(m) }).then((r) => r.module),
  remove: (token: string, clientId: string, id: string) =>
    request<{ success: boolean }>(`/api/admin/clients/${clientId}/io-modules/${id}`, token, { method: 'DELETE' }),
  setPanelDrawing: (token: string, clientId: string, show: boolean) =>
    request<{ success: boolean; show: boolean }>(`/api/admin/clients/${clientId}/io-panel-drawing`, token, { method: 'PUT', body: JSON.stringify({ show }) }),
  test: (token: string, clientId: string, body: Record<string, any>) =>
    request<IoTestResult>(`/api/admin/clients/${clientId}/io-modules/test`, token, { method: 'POST', body: JSON.stringify(body) }),
};
